import { DateTime } from 'luxon';
import { DocumentSearchDetailForm } from '../services/http/documents.api';
import documentManagementApi, { DocumentClassificationAndType } from '../services/http/documentManagement.api';
import TagApi, { BaseTag } from '../services/http/tag.api';
import { TagFilter } from '../components/SearchInputTop/SearchInputTopFilterModal';
import { WarehouseStatusCode } from './warehouse.utils';
import TagFormat from './tagFormat';

export enum SharedLinkPath {
  DOCUMENTS_SEARCH_FOLDER = 'Documents/searchFolder',
  FOLDER_SEARCH = 'Folder/search',
  DOCUMENTS_SEARCH_DETAIL = 'Documents/searchDetail',
  DOCUMENTS_SEARCH_PDF_DETAIL = 'Documents/searchPdfDetail',
  REMINDER_SYSTEM_TAG_LIMIT_EXPIRED = 'Reminder/systemTagLimitExpired',
}

export interface ResolvedSharedLinkData {
  form: DocumentSearchDetailForm;
  classification?: {
    classification: DocumentClassificationAndType;
    type: DocumentClassificationAndType['types'][number];
  };
  selectedClassification: DocumentClassificationAndType | null;
  tagFilters: TagFilter[];
  enableFolderSearch: boolean;
  warehouseStatus?: WarehouseStatusCode;
}

export default {
  mapParamsToSearchDetailForm(params: Record<string, unknown>): DocumentSearchDetailForm {
    const tagList = params.tagList as { tagSettingId?: number, tagLabel?: string, condition?: string, value1?: string, value2?: string }[] | undefined;

    const parseDate = (val: unknown): DateTime | undefined => {
      if (!val || typeof val !== 'string') return undefined;
      return DateTime.fromISO(val);
    };

    const parseNumber = (val: unknown): number | undefined => {
      if (val == null) return undefined;
      const num = Number(val);
      return Number.isNaN(num) ? undefined : num;
    };

    const result: DocumentSearchDetailForm = {
      ...params,
      name: params.documentName as string | undefined,
      fileName: params.documentFileName as string | undefined,
      ccControlNumber: params.ccControlNumber != null ? String(params.ccControlNumber) : undefined,
      searchRange: params.searchRange != null ? Number(params.searchRange) : undefined,
      includePdf: parseNumber(params.includePdf),
      includeAttachment: parseNumber(params.includeAttachment),
      electronicContract: parseNumber(params.electronicContract) as 1 | 0 | undefined,
      existPaper: parseNumber(params.existPaper) as 1 | 0 | undefined,
      warehouseStatus: params.warehouseStatus as WarehouseStatusCode | undefined,
      registDate_From: parseDate(params.registDate_From),
      registDate_To: parseDate(params.registDate_To),
      updateDate_From: parseDate(params.updateDate_From),
      updateDate_To: parseDate(params.updateDate_To),
      tagList: tagList?.map((tag) => ({
        tagSettingId: tag.tagSettingId,
        label: tag.tagLabel,
        condition: tag.condition,
        value1: tag.value1,
        value2: tag.value2,
      })),
    };

    return result;
  },

  findClassificationByTypeId(
    list: DocumentClassificationAndType[],
    documentTypeId: number,
  ): { classification: DocumentClassificationAndType, type: DocumentClassificationAndType['types'][number] } | undefined {
    let result: { classification: DocumentClassificationAndType, type: DocumentClassificationAndType['types'][number] } | undefined;
    list.some((cls) => {
      const foundType = cls.types.find((t) => t.documentTypeId === documentTypeId);
      if (foundType) {
        result = { classification: cls, type: foundType };
        return true;
      }
      return false;
    });
    return result;
  },

  convertTagListToTagFilters(
    tagList: DocumentSearchDetailForm['tagList'],
    allTags: BaseTag[],
  ): TagFilter[] {
    if (!tagList?.length) return [];

    const parseDate = (val: string | undefined): DateTime | null => {
      if (!val) return null;
      const dt = DateTime.fromISO(val);
      return dt.isValid ? dt : null;
    };

    const tagFilterMap = new Map<string, TagFilter>();

    tagList.forEach((tag) => {
      if (!tag.label && !tag.tagSettingId) return;
      const baseTag = allTags.find((t) => t.tagSettingId === tag.tagSettingId);
      if (!baseTag) return;

      const existing = tagFilterMap.get(baseTag.tagLabel);

      const tagEntry: TagFilter['tags'][number] = {
        condition: tag.condition || '',
        value1: tag.value1,
        value2: tag.value2,
      };

      if (baseTag.format === TagFormat.DATE) {
        tagEntry.date1 = parseDate(tag.value1);
        tagEntry.date2 = parseDate(tag.value2);
      }

      if (existing) {
        existing.tags.push(tagEntry);
      } else {
        tagFilterMap.set(baseTag.tagLabel, {
          defaultTag: baseTag,
          tags: [tagEntry],
        });
      }
    });

    return Array.from(tagFilterMap.values());
  },

  async resolveSharedLinkData(params: Record<string, unknown>): Promise<ResolvedSharedLinkData> {
    const form = this.mapParamsToSearchDetailForm(params);

    let classification: ResolvedSharedLinkData['classification'];
    let selectedClassification: DocumentClassificationAndType | null = null;
    let tagFilters: TagFilter[] = [];

    if (form.documentTypeId) {
      const classificationList = await documentManagementApi.getDocumentClassificationAndType();
      classification = this.findClassificationByTypeId(classificationList, form.documentTypeId);
      selectedClassification = classification?.classification ?? null;
    }

    if (form.tagList?.length) {
      const allTags = await TagApi.getOrganizationTags();

      // タグ名の更新と削除されたタグの除外
      const updatedTagList = form.tagList
        .map((tag) => {
          const currentTag = allTags.find((t) => t.tagSettingId === tag.tagSettingId);
          if (!currentTag) return null;
          return { ...tag, label: currentTag.tagLabel };
        })
        .filter((tag): tag is NonNullable<typeof tag> => tag !== null);

      form.tagList = updatedTagList;
      tagFilters = this.convertTagListToTagFilters(form.tagList, allTags);
    }

    return {
      form,
      classification,
      selectedClassification,
      tagFilters,
      enableFolderSearch: !!form.searchRange,
      warehouseStatus: form.warehouseStatus,
    };
  },
};
